import { useState } from 'react';
import './AgentParkedBanner.css';

// "This module is waiting on you", shown inside the module itself.
//
// The workflow ChatPanel already has an amber parked block — but it is mounted
// ONLY on the workflow tab, so the moment the user follows the link into the
// module they were asked about, the affordance to answer disappears. Boundary
// conditions are the case that makes this matter: face picking is deliberately
// panel-only, so the answer is "I did it", and the place to say so is the tile
// tab.
//
// It mounts ONCE, page-level, under the tab bar — not inside ChatPanel. That is
// not a layout preference: `renderMainContent` instantiates ChatPanel ~36 times
// (once per tile branch) and four branches have no ChatPanel at all, so putting it
// there would be 36 prop threadings and would still miss those four.
//
// Amber `#d97706`, matching the ChatPanel block, `GroupFrameNode`, `AutomationNode`
// and `TileStatusDot`, so "needs input" is one colour wherever it appears.

// The value sent by "Done — continue".
//
// A whole sentence because `runner.answer` interpolates it verbatim into the
// subagent's next turn, and that message already continues "…Re-read your module's
// current state before acting — they may have made the change in the panel rather
// than describing it." So this completes a sentence the backend is already writing.
export const DONE_IN_PANEL =
  "I've made the change in this module's panel. Re-read your current state and continue.";

export default function AgentParkedBanner({ parked, onAnswer }) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  if (!parked) return null;

  const options = parked.options || [];

  async function send(answer) {
    if (!answer || sending) return;
    setSending(true);
    try {
      await onAnswer(parked.tileId, answer);
      setValue('');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="agent-parked">
      <div className="agent-parked-body">
        <span className="agent-parked-glyph" aria-hidden="true">
          !
        </span>
        <div className="agent-parked-text">
          <p className="agent-parked-prompt">{parked.prompt}</p>
          {parked.why && <p className="agent-parked-why">{parked.why}</p>}
        </div>
      </div>

      <div className="agent-parked-actions">
        {options.length > 0 ? (
          // `POST /answer` REJECTS a value that is not one of the offered options
          // ("Answer must be one of: …"), so when there are options the canned
          // confirmation is not offered at all — it would 400. The buttons are what
          // replace it.
          options.map((opt) => (
            <button
              key={opt}
              type="button"
              className="agent-parked-option"
              disabled={sending}
              onClick={() => send(opt)}
            >
              {opt}
            </button>
          ))
        ) : (
          <button
            type="button"
            className="agent-parked-done"
            disabled={sending}
            onClick={() => send(DONE_IN_PANEL)}
          >
            Done — continue
          </button>
        )}

        <form
          className="agent-parked-form"
          onSubmit={(e) => {
            e.preventDefault();
            send(value.trim());
          }}
        >
          <input
            className="agent-parked-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Or type an answer…"
            disabled={sending}
          />
          <button type="submit" className="agent-parked-send" disabled={sending || !value.trim()}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
